/* !
 * hamburger adapted from:
 * @description Tasty CSS-animated hamburgers
 * @author Jonathan Suh @jonsuh
 * @site https://jonsuh.com/hamburgers
 * @link https://github.com/jonsuh/hamburgers
 */

import { ComponentMultiStyleConfig, SystemStyleObject } from "@chakra-ui/react";
import { StyleFunctionProps } from "@chakra-ui/theme-tools";

const config: {
	width: number;
	height: number;
	spacing: SystemStyleObject["bottom"];
	padding: number;
	strokeWidth: string;
	color: (isOpen: boolean) => string;
} = {
	width: 5,
	height: 4,
	spacing: "6px",
	padding: 4,
	strokeWidth: "1px",
	color: (isOpen) => (isOpen ? "white" : "var(--header-colors-primary)"),
};

const hamburgerLine: (isOpen: boolean) => SystemStyleObject = (isOpen) => ({
	display: "block",
	width: config.width,
	height: config.strokeWidth,
	backgroundColor: config.color(isOpen),
	position: "absolute",
});

const BurgerButton: ComponentMultiStyleConfig = {
	parts: ["hamburger", "hamburgerInner", "hamburgerBox"],
	baseStyle: ({ isOpen }: StyleFunctionProps) => ({
		hamburger: {
			minWidth: 10,
			minHeight: 10,
			display: "inline-flex",
			justifyContent: "center",
			alignItems: "center",
			transitionProperty: "opacity, filter",
			transitionDuration: "0.15s",
			transitionTimingFunction: "linear",
			font: "inherit",
			color: "inherit",
			textTransform: "none",
			backgroundColor: "transparent",
			border: 0,
			margin: 0,
			overflow: "visible",
			_hover: {
				backgroundColor: "whiteAlpha.200",
			},
			_focus: {
				borderRadius: 0,
				outline: "none",
				backgroundColor: "none",
			},
			_focusVisible: {
				boxShadow: "outline",
			},
		},
		hamburgerInner: {
			...hamburgerLine(isOpen),
			top: "50%",
			marginTop: `-${config.strokeWidth}`,
			transitionProperty: "transform",
			transitionDuration: "0.075s",
			transitionTimingFunction: "cubic-bezier(0.55, 0.055, 0.675, 0.19)",
			_before: {
				...hamburgerLine(isOpen),
				content: "''",
				top: `-${config.spacing}`,
				transition: "top 0.075s 0.12s ease, opacity 0.075s ease",
			},
			_after: {
				...hamburgerLine(isOpen),
				content: "''",
				bottom: `-${config.spacing}`,
				transition:
					"bottom 0.075s 0.12s ease, transform 0.075s cubic-bezier(0.55, 0.055, 0.675, 0.19)",
			},

			".is-active &": {
				backgroundColor: config.color(isOpen),
				transform: "rotate(45deg)",
				transitionDelay: "0.12s",
				transitionTimingFunction: "cubic-bezier(0.215, 0.61, 0.355, 1)",
				_before: {
					backgroundColor: config.color(isOpen),
					top: 0,
					opacity: 0,
					transition: "top 0.075s ease, opacity 0.075s 0.12s ease",
				},
				_after: {
					backgroundColor: config.color(isOpen),
					bottom: 0,
					transform: "rotate(-90deg)",
					transition:
						" bottom 0.075s ease, transform 0.075s 0.12s cubic-bezier(0.215, 0.61, 0.355, 1)",
				},
			},
		},
		hamburgerBox: {
			width: config.width,
			height: config.height,
			display: "inline-block",
			position: "relative",
		},
	}),
};

export default BurgerButton;
